import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical } from 'lucide-react';

interface MoreButtonOption {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  className?: string;
}

interface MoreButtonProps {
  options: MoreButtonOption[];
  buttonClassName?: string;
  menuClassName?: string;
  iconClassName?: string;
}

const MoreButton: React.FC<MoreButtonProps> = ({
  options,
  buttonClassName = '',
  menuClassName = '',
  iconClassName = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    
    // Recalcular posição ao abrir o menu
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom,
        right: window.innerWidth - rect.right
      });
    }
  };

  const handleOptionClick = (onClick: () => void) => {
    onClick();
    setIsOpen(false);
  };

  // Atualizar posição quando o componente montar, quando a janela for redimensionada ou quando rolar a página
  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom,
          right: window.innerWidth - rect.right
        });
      }
    };

    // Usar requestAnimationFrame para melhorar a performance durante a rolagem
    let ticking = false;
    const handleScrollAndResize = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updatePosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('resize', handleScrollAndResize);
    // Capturar eventos de rolagem em qualquer container
    window.addEventListener('scroll', handleScrollAndResize, true);
    
    updatePosition();
    
    return () => {
      window.removeEventListener('resize', handleScrollAndResize);
      window.removeEventListener('scroll', handleScrollAndResize, true);
    };
  }, [isOpen]);

  // Fechar o menu quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="inline-block">
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        className={`p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none ${buttonClassName}`}
        aria-label="Mais opções"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <MoreVertical className={`size-5 text-gray-500 dark:text-gray-400 ${iconClassName}`} />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className={`fixed z-[9999] rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-gray-200 dark:ring-gray-700 ring-opacity-5 focus:outline-none py-1 ${menuClassName}`}
          style={{
            top: `${menuPosition.top}px`,
            right: `${menuPosition.right}px`,
            width: 'max-content',
            maxHeight: '80vh', // Limitar altura máxima
            overflowY: 'auto'   // Permitir rolagem se necessário
          }}
          role="menu"
          aria-orientation="vertical"
        >
          {options.map((option, index) => (
            <button
              key={index}
              className={`w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 ${option.className || ''}`}
              onClick={() => handleOptionClick(option.onClick)}
              role="menuitem"
            >
              {option.icon}
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MoreButton;
